import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { useMediaQuery } from 'react-responsive'
import {
  DownloadOutlined,
  LeftOutlined,
  LockOutlined,
  MenuOutlined,
  MoreOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import { Dropdown, Input, Menu, Modal as AntdModal, Typography } from 'antd'
import { BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import WhiteCloseIcon from 'components/icons/WhiteCloseIcon'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Fix 1: Load the worker from the public folder to avoid Webpack 5 bundling issues that inject `require`
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'

const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent || ''
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  )
}

const getDevicePixelRatio = () => {
  const dpr = window.devicePixelRatio || 1
  const maxDpr = isIOS() ? 1.5 : 2.0
  return Math.min(dpr, maxDpr)
}

// Number of pages to render before/after the current page (virtual scrolling buffer)
// iOS: buffer=1 (3 canvases ~33MB) — strict ~300MB memory limit, avoid tab reload
// Desktop: buffer=2 (5 canvases ~55MB) — more RAM available, smoother pre-loading
const PAGE_BUFFER = isIOS() ? 1 : 2

type Props = {
  file: BuildingFile
  handleCloseFile: () => void
}

const documentOptions = {
  cMapUrl: '/cmaps/',
  cMapPacked: true
}

// ─────────────────────────────────────────────
// LazyThumbnail: renders a page thumbnail only when scrolled into view
// ─────────────────────────────────────────────
type LazyThumbnailProps = {
  pageNumber: number
  width: number
  isActive: boolean
  onClick: () => void
  thumbRef: (el: HTMLDivElement | null) => void
  isDocLoading: boolean
  imageUrl?: string
  isMainPageActive?: boolean
  isMainPageRendered?: boolean
}

function LazyThumbnail({
  pageNumber,
  width,
  isActive,
  onClick,
  thumbRef,
  isDocLoading,
  imageUrl = undefined,
  isMainPageActive = false,
  isMainPageRendered = false
}: LazyThumbnailProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    const node = containerRef.current
    if (!node || !window.IntersectionObserver) {
      setIsVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Delay thumbnail rendering by 500ms *after* the document has finished loading
  // To avoid concurrent rendering conflicts with the main view, we wait until the main view page renders if it is active.
  useEffect(() => {
    if (isVisible && !isDocLoading) {
      if (isMainPageActive && !isMainPageRendered) {
        return undefined
      }
      const timer = setTimeout(() => {
        setShouldRender(true)
      }, 500)
      return () => clearTimeout(timer)
    }
    setShouldRender(false)
    return undefined
  }, [isVisible, isDocLoading, isMainPageActive, isMainPageRendered])

  const renderThumbContent = () => {
    if (!shouldRender) {
      return (
        <div
          style={{
            width,
            height: width * 1.4,
            background: '#374151'
          }}
        />
      )
    }

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={`Page ${pageNumber}`}
          style={{ width, height: width * 1.4, objectFit: 'contain' }}
          className="pointer-events-none select-none"
          loading="lazy"
        />
      )
    }

    return (
      <Page
        pageNumber={pageNumber}
        width={width}
        devicePixelRatio={getDevicePixelRatio()}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    )
  }

  return (
    <div
      ref={(el) => {
        containerRef.current = el
        thumbRef(el)
      }}
      className="my-2 flex flex-col items-center cursor-pointer w-full"
      onClick={onClick}
      style={{ minHeight: width ? width * 1.4 + 28 : 112 }}
    >
      <div
        className={`p-1 rounded-sm ${
          isActive ? 'ring-2 ring-blue-400' : 'hover:ring-1 hover:ring-gray-400'
        }`}
      >
        {renderThumbContent()}
      </div>
      <div className="text-center text-xs text-white mt-1">{pageNumber}</div>
    </div>
  )
}

LazyThumbnail.defaultProps = {
  imageUrl: undefined,
  isMainPageActive: false,
  isMainPageRendered: false
}

// ─────────────────────────────────────────────
// LazyImagePage: renders static WebP image for pre-rasterized PDFs
// ─────────────────────────────────────────────
type LazyImagePageProps = {
  pageNumber: number
  isInWindow: boolean
  width: number
  pageRef: (el: HTMLDivElement | null) => void
  onVisible: (pageNumber: number) => void
  imageUrl: string
}

function LazyImagePage({
  pageNumber,
  isInWindow,
  width,
  pageRef,
  onVisible,
  imageUrl
}: LazyImagePageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = containerRef.current
    if (!node || !window.IntersectionObserver) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible(pageNumber)
        }
      },
      {
        root: null,
        threshold: 0.3
      }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [onVisible, pageNumber])

  // Preserved ratio height for A4/A3 is roughly 1.414
  const height = width * 1.414

  return (
    <div
      ref={(el) => {
        containerRef.current = el
        pageRef(el)
      }}
      className="my-3 flex flex-col items-center bg-white shadow-md relative"
      style={{
        width,
        height,
        minHeight: height
      }}
    >
      {isInWindow ? (
        <img
          src={imageUrl}
          alt={`Page ${pageNumber}`}
          className="w-full h-full object-contain pointer-events-none select-none"
          loading="lazy"
        />
      ) : (
        <div className="flex justify-center items-center h-full w-full bg-gray-100 text-gray-400">
          Loading...
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// LazyPdfPage: virtual scrolling – renders a canvas only when within the page buffer
// ─────────────────────────────────────────────
type LazyPdfPageProps = {
  pageNumber: number
  isInWindow: boolean
  isScrolling: boolean
  width: number
  pageRef: (el: HTMLDivElement | null) => void
  onVisible: (pageNumber: number) => void
  handleDownload: () => void
  onRenderSuccess?: () => void
  isActive: boolean
}

function LazyPdfPage({
  pageNumber,
  isInWindow,
  isScrolling,
  width,
  pageRef,
  onVisible,
  handleDownload,
  onRenderSuccess,
  isActive
}: LazyPdfPageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [renderedHeight, setRenderedHeight] = useState<number>(0)
  const [renderStatus, setRenderStatus] = useState<
    'loading' | 'success' | 'error'
  >('loading')
  const [delayRender, setDelayRender] = useState(false)

  // Delay rendering for buffer pages (not active) to prioritize the active page
  useEffect(() => {
    if (!isInWindow || isScrolling) {
      setDelayRender(false)
      return undefined
    }

    if (isActive) {
      setDelayRender(true)
      return undefined
    }

    const timer = setTimeout(() => {
      setDelayRender(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [isInWindow, isScrolling, isActive])

  // Track when the page scrolls into the viewport to update currentPage
  useEffect(() => {
    const node = containerRef.current
    if (!node || !window.IntersectionObserver) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible(pageNumber)
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [pageNumber, onVisible])

  const shouldRenderCanvas =
    isInWindow &&
    renderStatus !== 'error' &&
    (!isScrolling || renderStatus === 'success') &&
    (isActive || delayRender || renderStatus === 'success')

  // Setup render timeout (25s) and reset when page is out of window, not rendering, or successfully rendered
  useEffect(() => {
    if (!isInWindow) {
      setRenderStatus('loading')
      return undefined
    }

    if (renderStatus === 'error' || renderStatus === 'success') {
      return undefined
    }

    if (!shouldRenderCanvas) {
      return undefined
    }

    const timer = setTimeout(() => {
      setRenderStatus('error')
    }, 25000)

    return () => clearTimeout(timer)
  }, [isInWindow, renderStatus, shouldRenderCanvas])

  // Release canvas GPU memory when the page LEAVES the buffer window.
  // IMPORTANT: No cleanup function here! React runs the cleanup of a useEffect
  // AFTER the new render commits to DOM — meaning if isInWindow goes false→true,
  // <Page> mounts and creates its canvas FIRST, then the cleanup runs and would
  // zero the canvas mid-render, causing onRenderError immediately.
  // By having no cleanup here, we avoid zeroing a canvas that <Page> just created.
  useEffect(() => {
    if (!isInWindow && containerRef.current) {
      const canvases = containerRef.current.getElementsByTagName('canvas')
      for (let i = 0; i < canvases.length; i += 1) {
        canvases[i].width = 0
        canvases[i].height = 0
      }
    }
  }, [isInWindow])

  // Release canvas GPU memory on component unmount only (separate from above).
  // Empty deps ensures this only runs once, on unmount — never when isInWindow changes.
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        const canvases = containerRef.current.getElementsByTagName('canvas')
        for (let i = 0; i < canvases.length; i += 1) {
          canvases[i].width = 0
          canvases[i].height = 0
        }
      }
    }
  }, [])

  const devicePixelRatio = getDevicePixelRatio()

  const renderContent = (shouldRenderCanvas: boolean) => {
    if (shouldRenderCanvas) {
      return (
        <Page
          pageNumber={pageNumber}
          width={width}
          className="shadow-md"
          devicePixelRatio={devicePixelRatio}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onLoadSuccess={(page) => {
            // Save rendered height so the placeholder can preserve scroll height when this page leaves the window
            const h = (page.height / page.width) * width
            setRenderedHeight(h + 24) // +24 for my-3 margin
          }}
          onRenderSuccess={() => {
            setRenderStatus('success')
            onRenderSuccess?.()
          }}
          onRenderError={(err) => {
            // eslint-disable-next-line no-console
            console.error('Render page error:', err)
            setRenderStatus('error')
          }}
        />
      )
    }

    if (renderStatus === 'error') {
      return (
        // Fallback UI when rendering takes too long or fails
        <div
          style={{
            width: '100%',
            height: '100%',
            background: '#fee2e2',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            border: '1px solid #fca5a5',
            borderRadius: 4
          }}
        >
          <span className="text-red-600 font-semibold mb-2 text-center text-sm md:text-base">
            ページの描画に時間がかかっているか、エラーが発生しました。
          </span>
          <span className="text-gray-500 text-xs mb-4 text-center">
            このページを表示できません。ファイル全体をダウンロードしてご確認ください。
          </span>
          <button
            type="button"
            onClick={handleDownload}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
          >
            PDFをダウンロード
          </button>
        </div>
      )
    }

    return <span className="text-gray-500 text-sm">{pageNumber}</span>
  }

  const height = renderedHeight || width * 1.4

  return (
    <div
      ref={(el) => {
        containerRef.current = el
        pageRef(el)
      }}
      className="my-3 font-sans"
      style={{
        width,
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: shouldRenderCanvas ? 'transparent' : '#d1d5db'
      }}
    >
      {renderContent(shouldRenderCanvas)}
    </div>
  )
}

LazyPdfPage.defaultProps = {
  onRenderSuccess: undefined
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function ViewerPdf({ file, handleCloseFile }: Props) {
  const [numPages, setNumPages] = useState<number>(0)
  // currentPageRef: updated immediately on every scroll (no re-render)
  // currentPage state: debounced — only updates AFTER scrolling settles (150ms)
  // This prevents canvas spawn storms: when scrolling fast, isPageInWindow() does
  // NOT change rapidly, so pdf.worker doesn't create dozens of canvases in parallel.
  const currentPageRef = useRef<number>(1)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const currentPageDebounceRef = useRef<any>(null)
  const [scale, setScale] = useState<number>(1.0)
  const [loading, setLoading] = useState<boolean>(true)
  const [renderedPages, setRenderedPages] = useState<Record<number, boolean>>(
    {}
  )
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  const scrollTimeoutRef = useRef<any>(null)
  const [pageWidth, setPageWidth] = useState<number>(
    window.innerWidth > 500 ? 1000 : 350
  )
  // Ref for each page div to scroll to when clicking thumbnail
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [present, setPresent] = useState(false)
  const [showDocProps, setShowDocProps] = useState(false)
  // Ref for each thumbnail div to scroll into view
  const thumbRefs = useRef<(HTMLDivElement | null)[]>([])
  const isProgrammaticScroll = useRef(false)
  const programmaticScrollTimer = useRef<any>(null)
  const [pdfPassword, setPdfPassword] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const passwordCallbackRefs = useRef<any[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOpenLeftMenu, setIsOpenLeftMenu] = useState(true)
  const matches = useMediaQuery({ query: '(max-width: 768px)' })

  const handlePageRenderSuccess = useCallback((pageNo: number) => {
    setRenderedPages((prev) => ({ ...prev, [pageNo]: true }))
  }, [])

  const handleScroll = useCallback(() => {
    setIsScrolling((prev) => {
      if (!prev) return true
      return prev
    })

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false)
    }, 200)
  }, [])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (currentPageDebounceRef.current) {
        clearTimeout(currentPageDebounceRef.current)
      }
    }
  }, [])

  // When PDF is loaded, set the number of pages
  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n)
    if (currentPageDebounceRef.current)
      clearTimeout(currentPageDebounceRef.current)
    currentPageRef.current = 1
    setCurrentPage(1)
    setLoading(false)
    setShowPasswordModal(false)
    setPasswordError('')
    setPdfPassword('')
    passwordCallbackRefs.current = []
    pageRefs.current = Array(n).fill(null)

    // Lock programmatic scroll temporarily to allow layout stabilization
    isProgrammaticScroll.current = true
    if (programmaticScrollTimer.current) {
      clearTimeout(programmaticScrollTimer.current)
    }
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false
    }, 600)
  }

  // Handle initialization for pre-rasterized PDFs (Image Mode)
  useEffect(() => {
    if (file.extraInfo?.rasterized === 'true') {
      const pCount = Number(file.extraInfo?.page_count || 0)
      setNumPages(pCount)
      if (currentPageDebounceRef.current)
        clearTimeout(currentPageDebounceRef.current)
      currentPageRef.current = 1
      setCurrentPage(1)
      setLoading(false)
      pageRefs.current = Array(pCount).fill(null)

      // Lock programmatic scroll temporarily to allow layout stabilization
      isProgrammaticScroll.current = true
      if (programmaticScrollTimer.current) {
        clearTimeout(programmaticScrollTimer.current)
      }
      programmaticScrollTimer.current = setTimeout(() => {
        isProgrammaticScroll.current = false
      }, 600)
    }
  }, [file.extraInfo, file.id])

  // Reset scroll position to top when opening a new file
  useEffect(() => {
    setRenderedPages({})
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
      contentRef.current.scrollLeft = 0
    }
    // Lock programmatic scroll temporarily to allow layout stabilization
    isProgrammaticScroll.current = true
    if (programmaticScrollTimer.current) {
      clearTimeout(programmaticScrollTimer.current)
    }
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false
    }, 600)
  }, [file.id])

  const onDocumentLoadError = (error: any) => {
    setLoading(false)
    if (
      error?.name === 'PasswordException' ||
      error?.name === 'PasswordResponseException'
    ) {
      setShowPasswordModal(true)
      setPasswordError(
        error?.name === 'PasswordResponseException'
          ? 'パスワードが正しくありません'
          : ''
      )
    }
  }

  const [hasAgreedToOpen, setHasAgreedToOpen] = useState(false)
  const isHeavyPdf = () => {
    const size = file.extraInfo?.size || 0
    const pageCount = file.extraInfo?.page_count || 0
    // Heavy thresholds: size > 20MB (20971520 bytes) OR page_count > 100
    return size > 20 * 1024 * 1024 || pageCount > 100
  }

  const isHeavy = isHeavyPdf()
  const showHeavyWarning = isHeavy && !hasAgreedToOpen

  const updatePageWidth = useCallback(() => {
    if (!contentRef.current) return
    const containerWidth = contentRef.current.clientWidth - 84
    const calculatedWidth = Math.round(containerWidth * scale)
    setPageWidth(Math.min(calculatedWidth, 2000))
  }, [scale])

  useEffect(() => {
    updatePageWidth()
  }, [file.id, scale, isOpenLeftMenu, updatePageWidth])

  useEffect(() => {
    const handleResize = () => updatePageWidth()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updatePageWidth])

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.25, 3))
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5))

  // Scroll to the corresponding page when clicking thumbnail
  const handleClickThumbnail = (pageIdx: number) => {
    isProgrammaticScroll.current = true
    const newPage = pageIdx + 1
    // Cancel any pending debounce from scroll to avoid overwriting this click
    if (currentPageDebounceRef.current)
      clearTimeout(currentPageDebounceRef.current)
    currentPageRef.current = newPage
    setCurrentPage(newPage)

    if (programmaticScrollTimer.current) {
      clearTimeout(programmaticScrollTimer.current)
    }
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false
    }, 200)

    setTimeout(() => {
      const ref = pageRefs.current[pageIdx]
      if (ref) {
        ref.scrollIntoView({ behavior: 'auto', block: 'start' })
      }
    }, 50)
  }

  // Scroll to the page when changing page using header button
  const scrollToPage = (pageIdx: number) => {
    isProgrammaticScroll.current = true
    if (programmaticScrollTimer.current) {
      clearTimeout(programmaticScrollTimer.current)
    }
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false
    }, 200)

    const ref = pageRefs.current[pageIdx]
    if (ref) {
      ref.scrollIntoView({ behavior: 'auto', block: 'start' })
    }
  }

  const handlePrev = () => {
    setCurrentPage((p) => {
      const newPage = Math.max(p - 1, 1)
      if (currentPageDebounceRef.current)
        clearTimeout(currentPageDebounceRef.current)
      currentPageRef.current = newPage
      scrollToPage(newPage - 1)
      return newPage
    })
  }

  const handleNext = () => {
    setCurrentPage((p) => {
      const newPage = Math.min(p + 1, numPages)
      if (currentPageDebounceRef.current)
        clearTimeout(currentPageDebounceRef.current)
      currentPageRef.current = newPage
      scrollToPage(newPage - 1)
      return newPage
    })
  }

  // Fix 2: Download directly from s3Path instead of a blob stored in RAM
  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = file.s3Path
    a.download = file.name || 'document.pdf'
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const handleSetPageRef = (idx: number) => (el: HTMLDivElement | null) => {
    pageRefs.current[idx] = el
  }

  const handleSetThumbRef = (idx: number) => (el: HTMLDivElement | null) => {
    thumbRefs.current[idx] = el
  }

  // Callback from LazyPdfPage when a page scrolls into the viewport
  // Uses a ref for immediate update + debounced state to prevent canvas spawn storms.
  // When scrolling fast, currentPage state (which drives isPageInWindow) only updates
  // after the user pauses for 150ms — preventing dozens of canvases from being queued
  // simultaneously in the pdf.worker.
  const handlePageVisible = useCallback((pageNumber: number) => {
    if (isProgrammaticScroll.current) return
    currentPageRef.current = pageNumber

    if (currentPageDebounceRef.current) {
      clearTimeout(currentPageDebounceRef.current)
    }
    currentPageDebounceRef.current = setTimeout(() => {
      setCurrentPage(currentPageRef.current)
    }, 150)
  }, [])

  // When currentPage changes, scroll the thumbnail into view if hidden
  useEffect(() => {
    const ref = thumbRefs.current[currentPage - 1]
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [currentPage])

  const handlePassword = (callback: any) => {
    passwordCallbackRefs.current.push(callback)
    setShowPasswordModal(true)
  }

  const handlePasswordSubmit = () => {
    if (passwordCallbackRefs.current.length > 0) {
      passwordCallbackRefs.current.forEach((cb) => cb(pdfPassword))
    }
  }

  const menu = (
    <Menu className="pdf-menu-dark">
      <Menu.Divider />
      <Menu.Item key="present" onClick={() => setPresent(true)}>
        プレゼンテーション
      </Menu.Item>
      <Menu.Item key="docProps" onClick={() => setShowDocProps(true)}>
        ドキュメントのプロパティ
      </Menu.Item>
    </Menu>
  )

  const handleMenu = () => {
    setIsOpenLeftMenu(!isOpenLeftMenu)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartPosition({
      x: e.pageX - (contentRef.current?.scrollLeft || 0),
      y: e.pageY - (contentRef.current?.scrollTop || 0)
    })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    if (contentRef.current) {
      contentRef.current.scrollLeft = e.pageX - startPosition.x
      contentRef.current.scrollTop = e.pageY - startPosition.y
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const getThumbnailWidth = () => {
    if (!isOpenLeftMenu) return 0
    return matches ? 80 : 60
  }

  // Fix 3: Virtual scrolling – only render a canvas for pages within the buffer window
  const isPageInWindow = (pageNumber: number) => {
    return Math.abs(pageNumber - currentPage) <= PAGE_BUFFER
  }

  const getPageImageUrl = useCallback(
    (pageNo: number) => {
      if (!file.s3Path) return ''
      const folder =
        file.extraInfo?.raster_folder ||
        `${file.s3Path.replace(/\.[^/.]+$/, '')}/pages/`
      return `${folder}page-${pageNo}.webp`
    },
    [file.s3Path, file.extraInfo?.raster_folder]
  )

  // Fix 2: Pass the URL directly instead of a blob to enable progressive loading
  // Block loading the file until user agrees to open the heavy PDF
  const pdfSourceObj = useMemo(() => {
    if (showHeavyWarning || !file.s3Path) return null
    return {
      url: file.s3Path,
      disableRange: false,
      disableAutoFetch: true,
      disableStream: false
    }
  }, [file.s3Path, showHeavyWarning])

  const globalStyles = `
    .pdf-content-scroll,
    .pdf-content-scroll * {
      cursor: grab !important;
    }
    .pdf-content-scroll.cursor-grabbing,
    .pdf-content-scroll.cursor-grabbing * {
      cursor: grabbing !important;
    }
    .pdf-content-scroll a,
    .pdf-content-scroll [role='link'] {
      cursor: pointer !important;
    }
  `

  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.innerHTML = globalStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isRaster = file.extraInfo?.rasterized === 'true'

  const renderBodyContent = () => {
    return (
      <div className="flex bg-gray-500 h-[75vh] w-full">
        {/* Sidebar thumbnails – each thumbnail is lazy-loaded on scroll */}
        {isOpenLeftMenu && !matches && (
          <div className="bg-gray-800 py-3 px-1.5 flex flex-col items-center overflow-y-auto border-r border-[#333] w-[100px] sm:w-[120px] md:w-[140px]">
            <span className="text-xs text-white mb-3 px-1 text-center break-words w-full">
              {file.name}
            </span>
            {numPages > 0 &&
              Array.from({ length: numPages }, (_, idx) => (
                <LazyThumbnail
                  key={idx + 1}
                  pageNumber={idx + 1}
                  width={getThumbnailWidth()}
                  isActive={currentPage === idx + 1}
                  onClick={() => handleClickThumbnail(idx)}
                  thumbRef={handleSetThumbRef(idx)}
                  isDocLoading={loading}
                  imageUrl={isRaster ? getPageImageUrl(idx + 1) : undefined}
                  isMainPageActive={isPageInWindow(idx + 1)}
                  isMainPageRendered={!!renderedPages[idx + 1]}
                />
              ))}
          </div>
        )}

        {/* Main PDF content – virtual scrolling, only renders pages within the buffer */}
        <div
          ref={contentRef}
          className={`flex-1 overflow-auto bg-gray-300 ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            height: '100%',
            position: 'relative',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onScroll={handleScroll}
        >
          {loading && (
            <div className="relative flex justify-center items-center h-full w-full">
              <Spinner />
            </div>
          )}
          <div className="flex flex-col items-center p-2">
            {numPages > 0 &&
              Array.from({ length: numPages }, (_, idx) =>
                isRaster ? (
                  <LazyImagePage
                    key={idx + 1}
                    pageNumber={idx + 1}
                    isInWindow={isPageInWindow(idx + 1)}
                    width={pageWidth}
                    pageRef={handleSetPageRef(idx)}
                    onVisible={handlePageVisible}
                    imageUrl={getPageImageUrl(idx + 1)}
                  />
                ) : (
                  <LazyPdfPage
                    key={idx + 1}
                    pageNumber={idx + 1}
                    isInWindow={isPageInWindow(idx + 1)}
                    width={pageWidth}
                    pageRef={handleSetPageRef(idx)}
                    onVisible={handlePageVisible}
                    handleDownload={handleDownload}
                    onRenderSuccess={() => handlePageRenderSuccess(idx + 1)}
                    isScrolling={isScrolling}
                    isActive={currentPage === idx + 1}
                  />
                )
              )}
          </div>
        </div>
      </div>
    )
  }

  if (showHeavyWarning) {
    return (
      <AntdModal
        open={showHeavyWarning}
        onCancel={handleCloseFile}
        onOk={() => setHasAgreedToOpen(true)}
        okText="続ける"
        cancelText="キャンセル"
        title="警告"
        centered
        maskClosable={false}
        closable={false}
      >
        <div className="py-2">
          <p className="text-gray-700 font-sans">
            このファイルはサイズが大きくページ数が多いため、表示に時間がかかるか、端末の動作が不安定になる可能性があります。表示を続けますか？
          </p>
        </div>
      </AntdModal>
    )
  }

  // Present mode
  if (present) {
    return (
      <>
        <div
          className="ant-modal-mask fixed inset-0 z-[999]"
          style={{ background: 'rgba(0,0,0,0.45)' }}
        />
        <Modal
          open
          className="read-pdf-modal relative"
          width="90%"
          style={{ top: 20, padding: 0 }}
          onCancel={() => setPresent(false)}
          footer={null}
          centered
          maskClosable
          destroyOnClose
          bodyStyle={{ padding: 0 }}
        >
          <div className="flex flex-col items-center justify-center bg-[#181818] h-[70vh] w-full">
            <button
              type="button"
              onClick={() => setPresent(false)}
              className="absolute top-2 left-2 z-50 rounded text-xs font-medium bg-[#3c3a3a] hover:bg-[#4a4848] text-white py-1.5 px-3 transition-colors"
            >
              プレゼンテーションを終了
            </button>
            <div className="h-full w-full overflow-auto">
              {file.extraInfo?.rasterized === 'true' ? (
                <div className="flex flex-col items-center w-full p-2">
                  {Array.from({ length: numPages }, (_, idx) => (
                    <LazyImagePage
                      key={idx + 1}
                      pageNumber={idx + 1}
                      isInWindow={isPageInWindow(idx + 1)}
                      width={pageWidth}
                      pageRef={handleSetPageRef(idx)}
                      onVisible={handlePageVisible}
                      imageUrl={getPageImageUrl(idx + 1)}
                    />
                  ))}
                </div>
              ) : (
                <Document
                  file={pdfSourceObj}
                  options={documentOptions}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  onPassword={handlePassword}
                  loading={<Spinner />}
                  className="flex flex-col items-center w-full p-2"
                >
                  {Array.from({ length: numPages }, (_, idx) => (
                    <LazyPdfPage
                      key={idx + 1}
                      pageNumber={idx + 1}
                      isInWindow={isPageInWindow(idx + 1)}
                      width={pageWidth}
                      pageRef={handleSetPageRef(idx)}
                      onVisible={handlePageVisible}
                      handleDownload={handleDownload}
                      isScrolling={isScrolling}
                      onRenderSuccess={() => handlePageRenderSuccess(idx + 1)}
                      isActive={currentPage === idx + 1}
                    />
                  ))}
                </Document>
              )}
            </div>
          </div>
        </Modal>
      </>
    )
  }

  return (
    <>
      <div
        className="ant-modal-mask fixed inset-0 z-[999]"
        onClick={handleCloseFile}
        style={{ background: 'rgba(0,0,0,0.45)' }}
      />

      <Modal
        open
        className="read-pdf-modal relative"
        width="90%"
        style={{ top: 20, padding: 0 }}
        onCancel={handleCloseFile}
        footer={null}
        centered
        maskClosable
        destroyOnClose
        bodyStyle={{ padding: 0 }}
      >
        <div
          className={`absolute ${
            !matches ? '-top-10 right-2' : '-top-8 right-0'
          } `}
        >
          <Button
            onClick={handleCloseFile}
            icon={
              <WhiteCloseIcon className={matches ? 'scale-50' : 'scale-75'} />
            }
            size="small"
            type="text"
            className="group"
          />
        </div>
        {/* Header */}
        <div className="w-full flex items-center justify-between px-1 md:px-6 py-1 md:py-3 bg-[#3c3a3a] border-b border-[#333] min-h-[48px] md:min-h-[64px]">
          {/* Left */}
          {!matches && (
            <div className="hidden md:flex items-center gap-4 min-w-0">
              <MenuOutlined
                className="text-white !text-xl"
                onClick={handleMenu}
              />
              <span className="text-white text-sm font-bold truncate max-w-[300px]">
                {file.name}
              </span>
            </div>
          )}

          {/* Center */}
          <div className="flex items-center gap-1 md:gap-2 md:mx-auto overflow-x-auto">
            <Button
              icon={
                <LeftOutlined
                  className={`${
                    currentPage <= 1 ? 'text-gray-500' : 'text-white'
                  } !text-lg md:!text-xl`}
                />
              }
              onClick={handlePrev}
              disabled={currentPage <= 1}
              size="small"
              type="text"
            />

            <span className="text-white text-xs md:text-base font-semibold whitespace-nowrap">
              {currentPage} / {numPages || '?'}
            </span>

            <Button
              icon={
                <RightOutlined
                  className={`${
                    currentPage >= numPages ? 'text-gray-500' : 'text-white'
                  } !text-lg md:!text-xl`}
                />
              }
              onClick={handleNext}
              disabled={currentPage >= numPages}
              size="small"
              type="text"
            />

            <span className="mx-1 md:mx-2 text-white">|</span>

            <Button
              icon={
                <ZoomOutOutlined className="!text-white !text-lg md:!text-xl" />
              }
              onClick={handleZoomOut}
              size="small"
              type="text"
            />

            <span className="text-white text-xs md:text-base font-semibold whitespace-nowrap">
              {Math.round(scale * 100)}%
            </span>

            <Button
              icon={
                <ZoomInOutlined className="!text-white !text-lg md:!text-xl" />
              }
              onClick={handleZoomIn}
              size="small"
              type="text"
            />
          </div>

          {/* Right */}
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              icon={
                <DownloadOutlined className="text-gray-300 !text-lg md:!text-xl" />
              }
              onClick={handleDownload}
              size="small"
              type="text"
            />

            <Dropdown
              overlay={menu}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                icon={
                  <MoreOutlined className="text-gray-300 !text-lg md:!text-xl" />
                }
                shape="circle"
                type="text"
              />
            </Dropdown>
          </div>
        </div>

        {isRaster ? (
          renderBodyContent()
        ) : (
          <Document
            file={pdfSourceObj}
            options={documentOptions}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onPassword={handlePassword}
            loading={
              <div className="flex bg-gray-500 h-[75vh] w-full justify-center items-center">
                <Spinner />
              </div>
            }
            error={
              <div className="flex items-center justify-center h-[75vh] w-full bg-gray-100">
                <Typography.Text type="danger">
                  PDFの読み込みに失敗しました。
                </Typography.Text>
              </div>
            }
          >
            {renderBodyContent()}
          </Document>
        )}

        {/* Modal document properties */}
        {showDocProps && (
          <AntdModal
            open={showDocProps}
            onCancel={() => setShowDocProps(false)}
            footer={null}
            title="Document properties"
          >
            <div className="space-y-2">
              <div>
                <b>File name:</b> {file.name}
              </div>
              <div>
                <b>Pages:</b> {numPages}
              </div>
            </div>
          </AntdModal>
        )}

        {/* Modal password PDF */}
        {showPasswordModal && (
          <AntdModal
            open={showPasswordModal}
            onCancel={() => {
              setShowPasswordModal(false)
              handleCloseFile()
            }}
            onOk={handlePasswordSubmit}
            okText="OK"
            title={null}
            footer={null}
            centered
            destroyOnClose
            bodyStyle={{ padding: 32, textAlign: 'center' }}
            transitionName="ant-fade"
          >
            <div className="flex flex-col items-center justify-center gap-4">
              <LockOutlined
                style={{ fontSize: 40, color: '#1677ff', marginBottom: 8 }}
              />
              <div className="text-lg font-semibold mb-2">
                PDFパスワードが必要です
              </div>
              <div className="text-gray-500 mb-4">
                このPDFファイルを表示するにはパスワードを入力してください。
              </div>

              <Input.Password
                onChange={(e) => setPdfPassword(e.target.value)}
                autoComplete="new-password"
                size="middle"
                placeholder="PDFパスワードを入力"
                maxLength={256}
              />
              {passwordError && (
                <div
                  className="text-red-500 mt-2"
                  style={{ minHeight: 24, marginTop: 12, marginBottom: 0 }}
                >
                  {passwordError}
                </div>
              )}
              <Button
                type="primary"
                size="large"
                className="mt-4 w-full max-w-xs"
                onClick={handlePasswordSubmit}
                block
              >
                OK
              </Button>
            </div>
          </AntdModal>
        )}
      </Modal>
    </>
  )
}
